/**
 * Notify Me When Back in Stock — JavaScript
 * Vanilla JS, no dependencies.
 *
 * Handles:
 *  - Modal open/close with focus trapping
 *  - Email validation
 *  - API submission to Klaviyo via app proxy
 *  - Variant change listener for PDP
 */
(function () {
  'use strict';

  /* ── Selectors ─────────────────────────────────────────── */
  const SELECTORS = {
    trigger: '[data-notify-me-trigger]',
    overlay: '[data-notify-me-overlay]',
    modal: '[data-notify-me-modal]',
    close: '[data-notify-me-close]',
    form: '[data-notify-me-form]',
    emailInput: '[data-notify-me-email]',
    emailError: '[data-notify-me-email-error]',
    submitBtn: '[data-notify-me-submit]',
    submitText: '[data-notify-me-submit-text]',
    errorBanner: '[data-notify-me-error-banner]',
    stateForm: '[data-notify-me-state="form"]',
    stateSuccess: '[data-notify-me-state="success"]',
    successMessage: '[data-notify-me-success-message]',
    // Hidden fields
    fieldProductId: '[data-notify-me-field="product_id"]',
    fieldVariantId: '[data-notify-me-field="variant_id"]',
    fieldVariantSku: '[data-notify-me-field="variant_sku"]',
    fieldProductTitle: '[data-notify-me-field="product_title"]',
    fieldVariantTitle: '[data-notify-me-field="variant_title"]',
    fieldProductUrl: '[data-notify-me-field="product_url"]',
    fieldVariantImageUrl: '[data-notify-me-field="variant_image_url"]',
    // Product info display
    productImg: '[data-notify-me-product-img]',
    productName: '[data-notify-me-product-name]',
    productVariant: '[data-notify-me-product-variant]',
  };

  /* ── State ─────────────────────────────────────────────── */
  let previouslyFocusedElement = null;
  let overlay = null;

  /* ── Initialization ────────────────────────────────────── */
  function init() {
    overlay = document.querySelector(SELECTORS.overlay);
    if (!overlay) return;

    // Bind all trigger buttons (PDP + collection tiles)
    document.addEventListener('click', handleTriggerClick);

    // Close handlers
    const closeBtn = overlay.querySelector(SELECTORS.close);
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', handleBackdropClick);
    document.addEventListener('keydown', handleKeyDown);

    // Form submission
    // const form = overlay.querySelector(SELECTORS.form);
    // if (form) form.addEventListener('submit', handleSubmit);
    const submitBtn = overlay.querySelector(SELECTORS.submitBtn);
    if (submitBtn) submitBtn.addEventListener('click', handleSubmit, { once: true });

    // Email input — clear error on typing
    const emailInput = overlay.querySelector(SELECTORS.emailInput);
    if (emailInput) emailInput.addEventListener('input', clearEmailError);

    // Listen for Shopify variant change events
    listenForVariantChanges();
  }

  /* ── Trigger Click ─────────────────────────────────────── */
  function handleTriggerClick(e) {
    const trigger = e.target.closest(SELECTORS.trigger);
    if (!trigger) return;

    e.preventDefault();

    const context = {
      productId: trigger.dataset.productId || '',
      variantId: trigger.dataset.variantId || '',
      variantSku: trigger.dataset.variantSku || '',
      productTitle: trigger.dataset.productTitle || '',
      variantTitle: trigger.dataset.variantTitle || '',
      productUrl: trigger.dataset.productUrl || '',
      variantImageUrl: trigger.dataset.variantImageUrl || '',
    };

    openModal(context);
  }

  /* ── Open Modal ────────────────────────────────────────── */
  function openModal(context) {
    if (!overlay) return;

    // Populate hidden fields
    setField(SELECTORS.fieldProductId, context.productId);
    setField(SELECTORS.fieldVariantId, context.variantId);
    setField(SELECTORS.fieldVariantSku, context.variantSku);
    setField(SELECTORS.fieldProductTitle, context.productTitle);
    setField(SELECTORS.fieldVariantTitle, context.variantTitle);
    setField(SELECTORS.fieldProductUrl, context.productUrl);
    setField(SELECTORS.fieldVariantImageUrl, context.variantImageUrl);

    // Populate product info display
    const productImg = overlay.querySelector(SELECTORS.productImg);
    const productName = overlay.querySelector(SELECTORS.productName);
    const productVariant = overlay.querySelector(SELECTORS.productVariant);

    if (productImg && context.variantImageUrl) {
      productImg.src = context.variantImageUrl;
      productImg.alt = context.productTitle;
      productImg.parentElement.style.display = '';
    } else if (productImg) {
      productImg.parentElement.style.display = 'none';
    }

    if (productName) productName.textContent = context.productTitle;
    if (productVariant) productVariant.textContent = context.variantTitle;

    // Reset to form state
    resetModalState();

    // Store focus and open
    previouslyFocusedElement = document.activeElement;
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Focus the email input
    requestAnimationFrame(() => {
      const emailInput = overlay.querySelector(SELECTORS.emailInput);
      if (emailInput) emailInput.focus();
    });
  }

  /* ── Close Modal ───────────────────────────────────────── */
  function closeModal() {
    if (!overlay) return;

    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Restore focus
    if (previouslyFocusedElement) {
      previouslyFocusedElement.focus();
      previouslyFocusedElement = null;
    }
  }

  /* ── Backdrop Click ────────────────────────────────────── */
  function handleBackdropClick(e) {
    if (e.target === overlay) {
      closeModal();
    }
  }

  /* ── Keyboard ──────────────────────────────────────────── */
  function handleKeyDown(e) {
    if (!overlay || overlay.getAttribute('aria-hidden') !== 'false') return;

    if (e.key === 'Escape') {
      closeModal();
      return;
    }

    // Focus trap
    if (e.key === 'Tab') {
      trapFocus(e);
    }
  }

  /* ── Focus Trap ────────────────────────────────────────── */
  function trapFocus(e) {
    const modal = overlay.querySelector(SELECTORS.modal);
    if (!modal) return;

    const focusableSelectors = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusableElements = modal.querySelectorAll(focusableSelectors);
    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  /* ── Form Submission ───────────────────────────────────── */
  async function handleSubmit(e) {
    e.preventDefault();

    const emailInput = overlay.querySelector(SELECTORS.emailInput);
    if (!emailInput) return;

    // Validate email
    if (!validateEmail(emailInput)) return;

    // Build payload
    const payload = {
      email: emailInput.value.trim(),
      product_id: getFieldValue(SELECTORS.fieldProductId),
      variant_id: getFieldValue(SELECTORS.fieldVariantId),
      variant_sku: getFieldValue(SELECTORS.fieldVariantSku),
      product_title: getFieldValue(SELECTORS.fieldProductTitle),
      variant_title: getFieldValue(SELECTORS.fieldVariantTitle),
      product_url: getFieldValue(SELECTORS.fieldProductUrl),
      variant_image_url: getFieldValue(SELECTORS.fieldVariantImageUrl),
    };

    // Set loading state
    setLoadingState(true);
    hideErrorBanner();

    try {
      await submitToAPI(payload);

      // Show success state
      showState('success');
    } catch (err) {
      // Show error state (keep form visible)
      showErrorBanner(err.message || 'Something went wrong. Please try again.');
      setLoadingState(false);
    }
  }

  /* ── API Submission ────────────────────────────────────── */
  // The endpoint is served by the Shopify app backend via app proxy.
  // Adjust SUBSCRIBE_ENDPOINT if your app proxy base path differs.
  var SUBSCRIBE_ENDPOINT = '/apps/klaviyo-restock/api/back-in-stock/subscribe';

  async function submitToAPI(payload) {
    var response = await fetch(SUBSCRIBE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    var data;
    try {
      data = await response.json();
    } catch (_e) {
      throw new Error('Unexpected server response. Please try again.');
    }

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Something went wrong. Please try again.');
    }

    return data;
  }

  /* ── Email Validation ──────────────────────────────────── */
  function validateEmail(input) {
    const value = input.value.trim();
    const emailError = overlay.querySelector(SELECTORS.emailError);
    // Basic email regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!value) {
      showEmailError(input, emailError, 'Please enter your email address.');
      return false;
    }

    if (!emailRegex.test(value) || !input.checkValidity()) {
      showEmailError(input, emailError, 'Please enter a valid email address.');
      return false;
    }

    clearEmailError();
    return true;
  }

  function showEmailError(input, errorEl, message) {
    if (input) input.classList.add('notify-me-form__input--error');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.add('notify-me-form__error--visible');
    }
  }

  function clearEmailError() {
    const emailInput = overlay.querySelector(SELECTORS.emailInput);
    const emailError = overlay.querySelector(SELECTORS.emailError);
    if (emailInput) emailInput.classList.remove('notify-me-form__input--error');
    if (emailError) emailError.classList.remove('notify-me-form__error--visible');
  }

  /* ── State Helpers ─────────────────────────────────────── */
  function setLoadingState(loading) {
    const submitBtn = overlay.querySelector(SELECTORS.submitBtn);
    const emailInput = overlay.querySelector(SELECTORS.emailInput);

    if (submitBtn) {
      submitBtn.disabled = loading;
      submitBtn.classList.toggle('notify-me-form__submit--loading', loading);
    }
    if (emailInput) emailInput.disabled = loading;
  }

  function showState(state) {
    const formState = overlay.querySelector(SELECTORS.stateForm);
    const successState = overlay.querySelector(SELECTORS.stateSuccess);

    if (state === 'success') {
      if (formState) formState.classList.remove('notify-me-modal__state--active');
      if (successState) successState.classList.add('notify-me-modal__state--active');
    } else {
      if (formState) formState.classList.add('notify-me-modal__state--active');
      if (successState) successState.classList.remove('notify-me-modal__state--active');
    }
  }

  function showErrorBanner(message) {
    const banner = overlay.querySelector(SELECTORS.errorBanner);
    if (banner) {
      banner.textContent = message;
      banner.classList.add('notify-me-error-banner--visible');
    }
  }

  function hideErrorBanner() {
    const banner = overlay.querySelector(SELECTORS.errorBanner);
    if (banner) banner.classList.remove('notify-me-error-banner--visible');
  }

  function resetModalState() {
    const emailInput = overlay.querySelector(SELECTORS.emailInput);
    if (emailInput) {
      emailInput.value = '';
      emailInput.disabled = false;
    }
    clearEmailError();
    hideErrorBanner();
    setLoadingState(false);
    showState('form');
  }

  /* ── Field Helpers ─────────────────────────────────────── */
  function setField(selector, value) {
    const el = overlay.querySelector(selector);
    if (el) el.value = value;
  }

  function getFieldValue(selector) {
    const el = overlay.querySelector(selector);
    return el ? el.value : '';
  }

  /* ── Variant Change Listener ────────────────────────────── */
  function listenForVariantChanges() {

    // ── Theme-specific: VARIANT_CHANGE on <product-variants> ──
    // This theme fires a custom 'VARIANT_CHANGE' event on each
    // <product-variants> element when the customer picks a new option.
    bindProductVariantsListeners();

    // Re-bind after Shopify section loads/re-renders
    document.addEventListener('shopify:section:load', function () {
      overlay = document.querySelector(SELECTORS.overlay);
      bindProductVariantsListeners();
    });

    // ── Fallback: Dawn / OS 2.0 standard event ──
    document.addEventListener('variant:changed', handleVariantChangeLegacy);
  }

  /**
   * Finds all <product-variants> elements on the page and
   * attaches the VARIANT_CHANGE listener to each.
   */
  function bindProductVariantsListeners() {
    var variantPickers = document.querySelectorAll('product-variants');
    variantPickers.forEach(function (picker) {
      // Guard against double-binding
      if (picker._notifyMeBound) return;
      picker._notifyMeBound = true;

      picker.addEventListener('VARIANT_CHANGE', function (e) {
        handleThemeVariantChange(picker, e);
      });
    });
  }

  /**
   * Handles this theme's VARIANT_CHANGE event.
   * Walks up to the parent <product-page> to scope the update
   * to the correct product card (works on both PDP and collection).
   */
  function handleThemeVariantChange(picker, e) {
    var productPage = picker.closest('product-page');
    if (!productPage) return;

    // Find the selected variant from the theme's JSON
    var variantJson = picker.querySelector('script[type="application/json"]');
    var variants = [];
    try {
      variants = JSON.parse(variantJson.textContent);
    } catch (_e) {
      return;
    }

    // Read selected options from the variant picker (select or radio)
    var selectedOptions = [];
    var selects = picker.querySelectorAll('select[data-js-product-variant-container]');
    selects.forEach(function (sel) {
      selectedOptions.push(sel.value);
    });

    // Also check radio buttons if no selects found
    if (selectedOptions.length === 0) {
      var radioContainers = picker.querySelectorAll('[data-js-product-variant-container="radio"]');
      radioContainers.forEach(function (container) {
        var checked = container.querySelector('input[type="radio"]:checked');
        if (checked) selectedOptions.push(checked.value);
      });
    }

    // Find matching variant by options
    var matchedVariant = null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var match = true;
      for (var j = 0; j < selectedOptions.length; j++) {
        if (v['option' + (j + 1)] !== selectedOptions[j]) {
          match = false;
          break;
        }
      }
      if (match) {
        matchedVariant = v;
        break;
      }
    }

    if (!matchedVariant) return;

    // Find notify-me wrapper(s) inside this product-page scope
    var wrappers = productPage.querySelectorAll('[data-notify-me-wrapper]');
    wrappers.forEach(function (wrapper) {
      var trigger = wrapper.querySelector(SELECTORS.trigger);
      if (!trigger) return;

      if (!matchedVariant.available) {
        wrapper.style.display = '';
        trigger.dataset.variantId = String(matchedVariant.id);
        trigger.dataset.variantTitle = matchedVariant.title || '';
        trigger.dataset.variantSku = matchedVariant.sku || '';
        if (matchedVariant.featured_image && matchedVariant.featured_image.src) {
          trigger.dataset.variantImageUrl = matchedVariant.featured_image.src;
        }
      } else {
        wrapper.style.display = 'none';
      }
    });
  }

  /**
   * Legacy handler for Dawn / OS 2.0 themes that fire variant:changed
   */
  function handleVariantChangeLegacy(e) {
    var variant = e.detail ? e.detail.variant : null;
    if (!variant) return;

    var triggers = document.querySelectorAll(SELECTORS.trigger + '[data-notify-me-pdp]');
    triggers.forEach(function (trigger) {
      var wrapper = trigger.closest('[data-notify-me-wrapper]');
      if (!wrapper) return;

      if (!variant.available) {
        wrapper.style.display = '';
        trigger.dataset.variantId = variant.id;
        trigger.dataset.variantTitle = variant.title || '';
        trigger.dataset.variantSku = variant.sku || '';
        if (variant.featured_image && variant.featured_image.src) {
          trigger.dataset.variantImageUrl = variant.featured_image.src;
        }
      } else {
        wrapper.style.display = 'none';
      }
    });
  }

  /* ── Boot ───────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
